import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { GoogleMap, useJsApiLoader, Marker, InfoWindow } from "@react-google-maps/api";
import { api } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Card, CardContent } from "@/components/ui/card";
import { AlertTriangle, MapPin, RefreshCw } from "lucide-react";
import { projectTypeLabel, STATUS_META } from "@/lib/constants";
import { Button } from "@/components/ui/button";

const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const MAP_LIBRARIES = [];  // must be stable across renders; leave empty (JS API only)

// Default center: roughly geographic center of continental US so first
// paint doesn't zoom to null-island (0,0) when there are no pins yet.
const DEFAULT_CENTER = { lat: 39.8283, lng: -98.5795 };
const DEFAULT_ZOOM = 4;

// Color-coded status pins so an office user can eyeball the state of
// each site without clicking. Values map to Google Maps' built-in
// colored icon PNGs — no custom asset hosting required.
const STATUS_PIN_COLOR = {
  draft: "yellow",
  safety_complete: "blue",
  field_complete: "blue",
  testing_complete: "blue",
  photo_complete: "blue",
  ready_for_review: "orange",
  approved: "green",
};
function statusPinUrl(status) {
  const color = STATUS_PIN_COLOR[status] || "red";
  return `https://maps.google.com/mapfiles/ms/icons/${color}-dot.png`;
}

export default function MapPage() {
  const navigate = useNavigate();
  const [data, setData] = useState({ points: [], missing_coords: [], point_count: 0, missing_count: 0 });
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);

  const { isLoaded, loadError } = useJsApiLoader({
    id: "gmap-script",
    googleMapsApiKey: MAPS_KEY || "",
    libraries: MAP_LIBRARIES,
  });

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get("/projects/map");
      setData(r.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, []);

  // Fit bounds around actual pins so the map opens zoomed to the work.
  // We keep a ref to the map instance because bounds have to be fitted
  // in TWO cases: (a) the map finished loading and there's already
  // data, and (b) new data arrived after the map was already up (e.g.
  // Refresh, or the /projects/map fetch completed after the map
  // rendered). Without the effect below we'd only fit-on-load.
  const mapRef = useRef(null);
  const fitToPoints = useCallback(() => {
    if (!mapRef.current || !window.google) return;
    const pts = data.points || [];
    if (pts.length === 0) return;
    const b = new window.google.maps.LatLngBounds();
    pts.forEach((p) => b.extend({ lat: p.latitude, lng: p.longitude }));
    mapRef.current.fitBounds(b, 60);
  }, [data.points]);
  useEffect(() => {
    if (isLoaded) fitToPoints();
  }, [isLoaded, fitToPoints]);

  const onMapLoad = (map) => {
    mapRef.current = map;
    fitToPoints();
  };

  if (!MAPS_KEY) {
    return (
      <AppShell>
        <MapHeader onRefresh={refresh} loading={loading} pointCount={data.point_count} missingCount={data.missing_count} />
        <Card className="bg-amber-950/30 border-amber-900" data-testid="map-key-missing">
          <CardContent className="p-6 space-y-2">
            <div className="font-display font-bold text-amber-200 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" /> Google Maps API key not configured
            </div>
            <div className="text-sm text-amber-100/80">
              Set <span className="font-mono text-amber-300">REACT_APP_GOOGLE_MAPS_API_KEY</span> in
              the frontend .env and redeploy. See the admin who set up FieldOps.
            </div>
          </CardContent>
        </Card>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <MapHeader onRefresh={refresh} loading={loading} pointCount={data.point_count} missingCount={data.missing_count} />

      {data.missing_count > 0 && (
        <Card className="bg-amber-950/20 border-amber-900 mb-4" data-testid="map-missing-coords">
          <CardContent className="p-4">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
              <div className="text-sm">
                <div className="font-display font-bold text-amber-200">
                  {data.missing_count} project{data.missing_count === 1 ? "" : "s"} missing coordinates
                </div>
                <div className="text-amber-100/70 text-xs mt-0.5">
                  These active projects aren't on the map because they don't have latitude/longitude set.
                  Open the project and add coordinates (or an address that gets geocoded) to include it here.
                </div>
                <details className="mt-2">
                  <summary className="text-xs text-amber-300 cursor-pointer hover:underline" data-testid="map-missing-list-toggle">
                    Show list
                  </summary>
                  <ul className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-1 text-xs">
                    {data.missing_coords.map((m) => (
                      <li key={m.id} className="text-amber-100/80">
                        <button
                          onClick={() => navigate(`/projects/${m.id}`)}
                          className="hover:underline text-left"
                          data-testid={`map-missing-link-${m.id}`}
                        >
                          <span className="font-mono text-amber-300">{m.site_number || "—"}</span>{" "}
                          {m.site_name} · {m.customer}
                          {m.address && <span className="text-amber-100/50"> · {m.address}</span>}
                        </button>
                      </li>
                    ))}
                  </ul>
                </details>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {loadError && (
        <Card className="bg-red-950/30 border-red-900">
          <CardContent className="p-6 text-sm text-red-200">
            Failed to load Google Maps: {String(loadError.message || loadError)}
          </CardContent>
        </Card>
      )}

      {!loadError && !isLoaded && (
        <div className="h-[70vh] bg-slate-900 border border-slate-800 rounded flex items-center justify-center text-slate-400">
          Loading map…
        </div>
      )}

      {isLoaded && (
        <div className="rounded overflow-hidden border border-slate-800" data-testid="map-container">
          <GoogleMap
            mapContainerStyle={{ width: "100%", height: "75vh" }}
            center={DEFAULT_CENTER}
            zoom={DEFAULT_ZOOM}
            onLoad={onMapLoad}
            options={{
              streetViewControl: false,
              mapTypeControl: true,
              fullscreenControl: true,
            }}
          >
            {data.points.map((p) => (
              <Marker
                key={p.id}
                position={{ lat: p.latitude, lng: p.longitude }}
                icon={statusPinUrl(p.status)}
                title={`${p.site_number} — ${p.site_name}`}
                onClick={() => setActiveId(p.id)}
              />
            ))}
            {activeId && (() => {
              const p = data.points.find((x) => x.id === activeId);
              if (!p) return null;
              const statusLabel = STATUS_META[p.status]?.label || p.status;
              const navUrl = `https://www.google.com/maps/dir/?api=1&destination=${p.latitude},${p.longitude}`;
              return (
                <InfoWindow
                  position={{ lat: p.latitude, lng: p.longitude }}
                  onCloseClick={() => setActiveId(null)}
                >
                  <div style={{ color: "#0f172a", minWidth: 220 }} data-testid={`map-infowindow-${p.id}`}>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>{p.site_name || "—"}</div>
                    <div style={{ fontFamily: "monospace", fontSize: 12, color: "#1d4ed8" }}>{p.site_number}</div>
                    <div style={{ fontSize: 12, marginTop: 4 }}>
                      {p.customer} · {projectTypeLabel(p.project_type)}
                    </div>
                    {p.foreman && (
                      <div style={{ fontSize: 12, color: "#475569" }}>Foreman: {p.foreman}</div>
                    )}
                    <div style={{ fontSize: 11, marginTop: 6, color: "#334155" }}>Status: {statusLabel}</div>
                    <div style={{ display: "flex", gap: 12, marginTop: 10 }}>
                      <a
                        href={navUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          fontSize: 12, color: "#059669", fontWeight: 700,
                          textDecoration: "underline", cursor: "pointer",
                        }}
                        data-testid={`map-navigate-${p.id}`}
                      >
                        ► Navigate here
                      </a>
                      <button
                        onClick={() => navigate(`/projects/${p.id}`)}
                        style={{
                          fontSize: 12, color: "#1d4ed8",
                          textDecoration: "underline", background: "none", border: 0, padding: 0, cursor: "pointer",
                        }}
                        data-testid={`map-open-project-${p.id}`}
                      >
                        Open project →
                      </button>
                    </div>
                  </div>
                </InfoWindow>
              );
            })()}
          </GoogleMap>
        </div>
      )}
    </AppShell>
  );
}

function MapHeader({ onRefresh, loading, pointCount = 0, missingCount = 0 }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
      <div>
        <div className="text-[10px] tracking-[0.25em] uppercase text-slate-400 font-display font-bold">Fieldwork</div>
        <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight" data-testid="map-heading">
          Active Projects Map
        </h1>
        <div className="text-xs text-slate-400 mt-1 flex items-center gap-3">
          <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5 text-blue-400"/> {pointCount} plotted</span>
          {missingCount > 0 && (
            <span className="inline-flex items-center gap-1 text-amber-300">
              <AlertTriangle className="w-3.5 h-3.5"/> {missingCount} missing coordinates
            </span>
          )}
        </div>
      </div>
      <Button onClick={onRefresh} disabled={loading} variant="outline" className="border-slate-700 bg-slate-900 hover:bg-slate-800" data-testid="map-refresh">
        <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`}/> Refresh
      </Button>
    </div>
  );
}
